import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * A page drawn as a desktop browser or a phone would lay it out, and scaled
 * down to fit the column it sits in — the frame both Site & widget previews
 * (the site, the widget on a business's site) are drawn in.
 *
 * Desktop is never narrower than the template's `lg`, where its full navigation
 * appears; a phone is a common 390px handset. The content is laid out at that
 * width (`children({ width, height })`) and scaled as a whole, so what fits on
 * one line on a laptop still does in the preview, only smaller.
 */
export const DESKTOP_WIDTH = 1024;
export const PHONE_WIDTH = 390;

const paneHeightFor = (viewportHeight) =>
  Math.round(Math.min(760, Math.max(460, viewportHeight * 0.7)));

/** On a phone a desktop page scaled to the screen is too small to read. */
export const initialDevice = (preferred) =>
  preferred ||
  (typeof window !== "undefined" && window.matchMedia?.("(max-width: 639px)").matches
    ? "mobile"
    : "desktop");

const deviceButtonClass = (active) =>
  `inline-flex min-h-11 cursor-pointer items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active ? "bg-white text-ink-900 shadow-card" : "text-ink-500 hover:text-ink-900"
  }`;

export const DeviceToggle = ({ device, onChange }) => {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t("Dashboard.MySite.preview.deviceLabel")}
      className="flex gap-1 rounded-lg bg-ink-100 p-1"
    >
      {[
        { value: "desktop", icon: "bi-display" },
        { value: "mobile", icon: "bi-phone" },
      ].map(({ value, icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={device === value}
          onClick={() => onChange(value)}
          className={deviceButtonClass(device === value)}
        >
          <i className={`bi ${icon}`} aria-hidden="true" />
          {t(`Dashboard.MySite.preview.device.${value}`)}
        </button>
      ))}
    </div>
  );
};

DeviceToggle.propTypes = {
  device: PropTypes.oneOf(["desktop", "mobile"]).isRequired,
  onChange: PropTypes.func.isRequired,
};

const DOT_COLORS = {
  plain: ["bg-ink-300", "bg-ink-300", "bg-ink-300"],
  traffic: ["bg-red-400", "bg-amber-400", "bg-green-500"],
};

const DeviceFrame = ({ device, bar = null, children }) => {
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [paneHeight, setPaneHeight] = useState(() =>
    paneHeightFor(typeof window === "undefined" ? 800 : window.innerHeight),
  );

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => {
      setBoxWidth(box.clientWidth);
      setPaneHeight(paneHeightFor(window.innerHeight));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  // Until the box has been measured (and in jsdom, where it never is), draw the
  // page at its own size rather than at a scale of zero.
  const bezel = device === "mobile" ? 12 : 2;
  const available = boxWidth
    ? boxWidth - bezel
    : device === "mobile"
      ? PHONE_WIDTH
      : DESKTOP_WIDTH;
  const width = device === "mobile" ? PHONE_WIDTH : Math.max(DESKTOP_WIDTH, available);
  const scale = Math.min(1, available / width);
  const height = Math.ceil(paneHeight / scale);

  return (
    <div ref={boxRef} className="w-full">
      <div
        className={`relative mx-auto overflow-hidden bg-white ${
          device === "mobile"
            ? "rounded-[1.75rem] border-[6px] border-ink-900 shadow-card-hover"
            : "rounded-xl border border-ink-200 shadow-card"
        }`}
        style={{ width: `${Math.floor(width * scale) + bezel}px`, maxWidth: "100%" }}
      >
        {device === "desktop" && bar && (
          <div className="flex items-center gap-2 border-b border-ink-200 bg-ink-50 px-3 py-2">
            <span className="flex gap-1" aria-hidden="true">
              {DOT_COLORS[bar.dots || "plain"].map((color, index) => (
                <span key={index} className={`size-2 rounded-full ${color}`} />
              ))}
            </span>
            {bar.address && (
              <span className="flex min-w-0 items-center gap-1.5 rounded-md bg-white px-2.5 py-0.5 text-xs text-ink-500">
                <i className="bi bi-lock-fill text-[0.65rem]" aria-hidden="true" />
                <span className="truncate">{bar.address}</span>
              </span>
            )}
          </div>
        )}
        <div className="relative overflow-hidden" style={{ height: `${paneHeight}px` }}>
          <div
            className="absolute top-0 left-0"
            style={{
              width: `${width}px`,
              height: `${height}px`,
              transform: scale < 1 ? `scale(${scale})` : undefined,
              transformOrigin: "0 0",
            }}
          >
            {children({ width, height })}
          </div>
        </div>
      </div>
    </div>
  );
};

DeviceFrame.propTypes = {
  device: PropTypes.oneOf(["desktop", "mobile"]).isRequired,
  /** Desktop only: a browser's top bar, with an address in it if given. */
  bar: PropTypes.shape({
    address: PropTypes.string,
    dots: PropTypes.oneOf(["plain", "traffic"]),
  }),
  /** Called with the width and height the content is laid out at. */
  children: PropTypes.func.isRequired,
};

export default DeviceFrame;
